import React, { useMemo } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  DEFAULT_COUNTRY,
  getCountryConfig,
  isSupportedCountry,
} from "../../utils/countryConfig";
import {
  nationalPhoneForStorage,
  phoneToE164,
} from "../../utils/phoneValidation";

// Intercepts the value passed by PhoneInput and strips the country calling code if it's there.
const CustomInput = React.forwardRef((props, ref) => {
  let { value, "data-dial-code": displayCode, ...rest } = props;

  if (value && displayCode) {
    if (value.startsWith(displayCode + " ")) {
      value = value.substring(displayCode.length + 1);
    } else if (value.startsWith(displayCode)) {
      value = value.substring(displayCode.length);
    }
  }

  return <input ref={ref} value={value || ""} {...rest} />;
});

/**
 * Phone input with country calling code. Stores national digits only (backward compatible).
 * Uses react-phone-number-input with libphonenumber-js validation at submit time.
 */
const PhoneNumberField = ({
  country = DEFAULT_COUNTRY,
  phoneCountryCode,
  value = "",
  onChange,
  name = "phoneNumber",
  id,
  disabled = false,
  placeholder = "Phone Number",
  className = "",
  inputClassName = "",
  testId,
  tabIndex,
}) => {
  const resolvedCountry = isSupportedCountry(String(country).toUpperCase())
    ? String(country).toUpperCase()
    : DEFAULT_COUNTRY;
  const config = getCountryConfig(resolvedCountry);
  const displayCode = config.phoneCountryCode;

  const e164Value = useMemo(
    () =>
      phoneToE164({
        country: resolvedCountry,
        phoneCountryCode: displayCode,
        phoneNumber: value,
      }) || undefined,
    [resolvedCountry, displayCode, value]
  );

  const handleChange = (nextE164) => {
    const national = nationalPhoneForStorage(nextE164 || "", resolvedCountry);
    onChange?.({
      target: { name, value: national },
    });
  };

  return (
    <div
      className={`phone-number-field ${className}`}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <div className="phone-country-code-prefix" aria-hidden="true">
        {displayCode}
      </div>
      <PhoneInput
        key={resolvedCountry}
        id={id || name}
        name={name}
        international={false}
        countryCallingCodeEditable={false}
        defaultCountry={resolvedCountry}
        countries={[resolvedCountry]}
        country={resolvedCountry}
        value={e164Value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        limitMaxLength
        countrySelectComponent={() => null}
        flagComponent={() => null}
        countrySelectProps={{ disabled: true, tabIndex: -1 }}
        inputComponent={CustomInput}
        numberInputProps={{
          className: inputClassName,
          tabIndex,
          "data-testid": testId,
          "data-dial-code": displayCode,
        }}
        className="PhoneInput--proteams PhoneInput--no-country-select"
      />
    </div>
  );
};

export default PhoneNumberField;

